import { Component, Prop, PropSync, Ref, Vue } from 'vue-property-decorator'
import DiagnosisConsultationModalEmptyState
    from '@/modules/diagnosisModule/views/form/modals/consultation/modal-empty-state/diagnosis-consultation-modal-empty-state.vue'
import DiagnosisConsultationModalToolbar
    from '@/modules/diagnosisModule/views/form/modals/consultation/modal-toolbar/diagnosis-consultation-modal-toolbar.vue'
import DiagnosisConsultationModalConsultationDisplay
    from '@/modules/diagnosisModule/views/form/modals/consultation/modal-consultation-display/diagnosis-consultation-modal-consultation-display.vue'
import DiagnosisConsultationModalConsultationNew
    from '@/modules/diagnosisModule/views/form/modals/consultation/modal-consultation-new/diagnosis-consultation-modal-consultation-new.vue'
import DiagnosisConsultationModalConsultationPersonnelInfo
    from '@/modules/diagnosisModule/views/form/modals/consultation/modal-consultation-personnel-info/diagnosis-consultation-modal-consultation-personnel-info.vue'
import { BModal } from 'bootstrap-vue'
import DiagnosisConsultationModalConsultationNewScript
    from '@/modules/diagnosisModule/views/form/modals/consultation/modal-consultation-new/diagnosis-consultation-modal-consultation-new'
import {
    DiagnosisConsultationModalState,
    initialDiagnosisConsultationModalState
} from '@/modules/diagnosisModule/views/form/modals/consultation/modal/diagnosis-consultation-modal.model'
import { diagnosisConsultationService } from '@/modules/diagnosisModule/services/diagnosis-consultation/diagnosis-consultation.service'
import {
    DiagnosisConsultationCreateRequest,
    DiagnosisConsultationFinalizationRequest,
    DiagnosisConsultationResponse,
    DiagnosisConsultationStatusEnum
} from '@/modules/diagnosisModule/services/diagnosis-consultation/diagnosis-consultation.dto'
import { CommonPersonnelResponse } from '@/api/services/common/personnel/common-personnel.dto'
import sessionStore from '@/session/sessionStore'
import { ConsultationToolbarSelectorTypeEnum } from '@/modules/diagnosisModule/views/form/modals/consultation/modal-toolbar/diagnosis-consultation-modal-toolbar.model'
import { orderBy, uniqBy } from 'lodash'

@Component({
    components: {
        DiagnosisConsultationModalEmptyState,
        DiagnosisConsultationModalToolbar,
        DiagnosisConsultationModalConsultationDisplay,
        DiagnosisConsultationModalConsultationNew,
        DiagnosisConsultationModalConsultationPersonnelInfo
    }
})
export default class DiagnosisConsultationModal extends Vue {
    @Ref() readonly consultationModal!: BModal
    @Ref() readonly newConsultationModal!: DiagnosisConsultationModalConsultationNewScript

    @Prop(Number) readonly caseId!: number
    @Prop(String) readonly caseNumber!: string
    @Prop(Number) readonly pathologistId!: number

    @PropSync('consultations', { type: Array, default: () => [] })
    syncedConsultations!: DiagnosisConsultationResponse[]

    get consultationsFiltered(): DiagnosisConsultationResponse[] {
        let consultations = this.syncedConsultations

        // filter by responsibility
        if (this.isConsultant) {
            consultations = consultations.filter(consultation => consultation.receiver.id === sessionStore.getUserPersonnelId())
        }

        // filter by status
        switch (this.state.selectorType) {
        case ConsultationToolbarSelectorTypeEnum.PENDING_CONSULTATIONS:
            consultations = consultations.filter(consultation => consultation.status.code === DiagnosisConsultationStatusEnum.PENDING)
            break
        case ConsultationToolbarSelectorTypeEnum.SOLVED_CONSULTATIONS:
            consultations = consultations.filter(consultation => consultation.status.code === DiagnosisConsultationStatusEnum.SOLVED)
            break
        }

        // order by most recent first
        consultations = orderBy(consultations, ['sendingDate'], ['desc'])

        return consultations
    }

    get consultants(): CommonPersonnelResponse[] {
        // consultations receivers without duplicates
        return uniqBy(this.syncedConsultations.map(consultation => consultation.receiver), 'id')
    }

    get isPathologist(): boolean {
        return this.pathologistId === sessionStore.getUserPersonnelId()
    }
    get isConsultant(): boolean {
        return !!this.syncedConsultations.filter(consultation => consultation.receiver.id === sessionStore.getUserPersonnelId()).length
    }

    get visibleConsultations(): boolean {
        return (this.isPathologist || this.isConsultant) &&
            this.state.selectorType !== ConsultationToolbarSelectorTypeEnum.CONSULTANTS
    }

    state: DiagnosisConsultationModalState
    constructor() {
        super()
        this.state = initialDiagnosisConsultationModalState()
    }

    /// SERVICES CALLS ///

    private async _getConsultations(caseId: number): Promise<DiagnosisConsultationResponse[]> {
        const response = await this.$_toCallUnique(diagnosisConsultationService.getConsultations(this.state.laboratoryId, caseId))
        return response ?? []
    }

    private async _createConsultation(caseId: number, request: DiagnosisConsultationCreateRequest): Promise<DiagnosisConsultationResponse | null> {
        const response = await this.$_toCallUnique(diagnosisConsultationService.createConsultation(this.state.laboratoryId, caseId, request))
        if (response) {
            this.$_showToastSuccess(this.$tc('diagnosis.modals.consultRequested'))
            this._hideNewConsultModal()
        }
        return response
    }

    private async _patchConsultation(caseId: number, consultationId: number, request: DiagnosisConsultationFinalizationRequest): Promise<DiagnosisConsultationResponse | null> {
        const response = await this.$_toCallUnique(diagnosisConsultationService.patchConsultation(this.state.laboratoryId, caseId, consultationId, request))
        if (response) {
            this.$_showToastSuccess(this.$tc('diagnosis.modals.consultReplied'))
        }
        return response
    }

    private async _deleteConsultation(caseId: number, consultationId: number): Promise<boolean> {
        const response = await this.$_toCallUnique(diagnosisConsultationService.deleteConsultation(this.state.laboratoryId, caseId, consultationId))
        if (response !== null) {
            this.$_showToastSuccess(this.$tc('diagnosis.modals.consultDeleted'))
            return true
        }
        return false
    }

    /// OPERATIONS ///

    private async _loadConsultations(): Promise<void> {
        this.syncedConsultations = await this._getConsultations(this.caseId)
        this._updateConsultationSummary()
    }

    async createOperation(request: DiagnosisConsultationCreateRequest): Promise<void> {
        if (await this._createConsultation(this.caseId, request)) {
            await this._loadConsultations()
        }
    }

    async patchOperation(consultationId: number, request: DiagnosisConsultationFinalizationRequest): Promise<void> {
        if (await this._patchConsultation(this.caseId, consultationId, request)) {
            await this._loadConsultations()
        }
    }

    async deleteOperation(consultationId: number): Promise<void> {
        if (await this.$_showConfirmationModal(this.$tc('diagnosis.modals.deleteQuestion'))) {
            if (await this._deleteConsultation(this.caseId, consultationId)) {
                await this._loadConsultations()
            }
        }
    }

    /// MODAL ///

    async show(selectorType?: ConsultationToolbarSelectorTypeEnum): Promise<void> {
        this.state = initialDiagnosisConsultationModalState()
        if (selectorType) {
            this.state.selectorType = selectorType
        }

        this.consultationModal.show()
    }

    hide(): void {
        this.consultationModal.hide()
    }

    async showNewConsultModal(): Promise<void> {
        await this.newConsultationModal.show()
    }

    private _hideNewConsultModal(): void {
        this.newConsultationModal.hide()
    }

    private _updateConsultationSummary(): void {
        this.$eventHub.$emit('update-case-consultation-summary')
    }
}
